import {
  FormControl, FormLabel, Radio, RadioGroup, Stack, Box, Divider, Flex, Text, TypographyProps,
} from "@chakra-ui/react"
import React, { Fragment, ReactNode } from "react"
import { Control, useController } from "react-hook-form"

export type RadioButtonsOption = {
  label: string|ReactNode
  value: string
  disabled?: boolean
  additionalElement?: ReactNode
 }

type Props = {
  name: string
  control: Control<any>
  options: RadioButtonsOption[]
  divider?: boolean
  horizontal?: boolean
  label?: string
  labelSize?: TypographyProps["fontSize"]
  mb?: number
  onChange?: (e) => void
  px?: number
  spacing?: number
  submitOnChange?: boolean
  /** Gap between radio+label and additionalElement per option. Default 0 = tight layout. */
  optionGap?: number
  /** Radio-vs-label vertical alignment. Use "center" for single-line labels. */
  alignItems?: "flex-start" | "center"
  /** Options fill the row by default; "auto" keeps them side by side. */
  optionWidth?: "100%" | "auto"
}

const RadioButtons = ({
  name,
  control,
  options,
  divider = false,
  horizontal = false,
  label,
  labelSize = "md",
  mb = 4,
  onChange = () => {},
  px = 0,
  spacing = 3,
  submitOnChange = false,
  optionGap = 0,
  alignItems = "flex-start",
  optionWidth = "100%",
}: Props) => {
  const { field } = useController({ name, control })

  return (
    <FormControl mb={mb}>
      <Flex align="flex-start">
        {label
              && <FormLabel fontSize={labelSize} w={1 / 4}>{label}</FormLabel>}
        <RadioGroup
          w="100%"
          value={field.value}
          onChange={(value) => {
            onChange(value)
            field.onChange(value)
          }}
        >
          <Stack
            align="center"
            justify={horizontal && optionWidth === "auto" ? "flex-start" : undefined}
            direction={horizontal ? "row" : "column"}
            spacing={spacing}
            divider={divider ? <Divider /> : undefined}
          >
            {options.map((option) => {
              const optionContent = (
                <>
                  <Radio
                    variant={option.disabled ? "readonly" : undefined}
                    isDisabled={option.disabled}
                    value={option.value}
                    w={optionWidth}
                    px={px}
                    alignItems={alignItems}
                    onChange={(e) => {
                      if (submitOnChange && e.target.checked) {
                        e.target.form?.submit()
                      }
                    }}
                  >
                    <Box
                      ml={1}
                      mt={alignItems === "center" ? 0 : (horizontal ? "-3px" : "-6px")}
                      lineHeight={alignItems === "center" ? 2 : undefined}
                    >
                      {option.label}
                    </Box>
                  </Radio>
                  {option.additionalElement}
                </>
              )

              return optionGap > 0 ? (
                <Stack key={option.value} gap={optionGap} align="flex-start" w="100%">
                  {optionContent}
                </Stack>
              ) : (
                <Fragment key={option.value}>{optionContent}</Fragment>
              )
            })}
          </Stack>
        </RadioGroup>
      </Flex>
    </FormControl>
  )
}

type RadioOptionLabelProps = {
  title: string
  description?: ReactNode
  disabled?: boolean
  spacing?: number
}

export const RadioOptionLabel = ({
  title, description, disabled = false, spacing = 2,
}: RadioOptionLabelProps) => (
  <Stack mt={0.5} spacing={spacing}>
    <Text as="strong" fontWeight="semibold" opacity={disabled ? 0.4 : 1}>{title}</Text>
    {description && <Stack spacing={2}>{description}</Stack>}
  </Stack>
)

export default RadioButtons
